import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BlogData from "./BlogData";
import "./Home.css";
import AuthButton from "./AuthButton";
import { supabase } from "../supabaseClient";

const API = "https://alpha-apk-backend.onrender.com";

export default function Home() {
  const [apks, setApks] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCookie, setShowCookie] = useState(false);

  const navigate = useNavigate();

  const categories = [
    "All",
    "Games",
    "Tools",
    "Music",
    "Social",
    "Education",
    "VPN",
    "File Management",
    "AI Assistant",
    "Photo Editing"
  ];

  // Fetch APKs from backend
  useEffect(() => {
    fetch(`${API}/api/apks`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch (error) {
          console.log("API returned non-JSON:", text);
          throw new Error("Backend returned HTML instead of JSON");
        }
      })
      .then((data) => {
        console.log("APKS DATA:", data);
        setApks(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.log("FETCH ERROR:", err);
        setApks([]);
      });
  }, []);

  // Cookie consent check
  useEffect(() => {
    const accepted = localStorage.getItem("alphaCookieAccepted");
    if (!accepted) {
      setShowCookie(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("alphaCookieAccepted", "true");
    setShowCookie(false);
  };

  const filteredApks = apks.filter((apk) => {
    const matchesSearch = apk.name
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const cat = apk.category?.toLowerCase() || "";

    const matchesCategory =
      selectedCategory === "All" ||
      (selectedCategory === "Games" && cat.includes("game")) ||
      (selectedCategory === "Tools" && cat.includes("tool")) ||
      (selectedCategory === "Music" && cat.includes("music")) ||
      (selectedCategory === "Social" &&
        (cat.includes("social") || cat.includes("media"))) ||
      (selectedCategory === "Education" && cat.includes("education")) ||
      (selectedCategory === "VPN" && cat.includes("vpn")) ||
      (selectedCategory === "File Management" &&
        (cat.includes("file") || cat.includes("manager"))) ||
      (selectedCategory === "AI Assistant" &&
        (cat.includes("ai") || cat.includes("assistant"))) ||
      (selectedCategory === "Photo Editing" &&
        (cat.includes("photo") || cat.includes("edit")));

    return matchesSearch && matchesCategory;
  });

  const latestBlogs = BlogData.slice(0, 3);

  // Schema for homepage
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Alpha APK Store",
    "url": "https://www.alphaapkstore.com/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.alphaapkstore.com/?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Alpha APK Store",
    "alternateName": "ALPH",
    "url": "https://www.alphaapkstore.com/",
    "logo": "https://www.alphaapkstore.com/logo.png",
    "description": "Alpha APK Store is an Android APK discovery platform offering safe APK downloads, Android app information, compatibility guidance, app guides, tools, VPN apps, music apps, social apps and games.",
    "founder": {
      "@type": "Person",
      "name": "Karandeep Singh Sandhu"
    },
    "foundingDate": "2026",
    "email": "alphaapkstore@gmail.com",
    "sameAs": [
      "https://www.youtube.com/@alphaapkstore",
      "https://pin.it/4iXPuvWFH"
    ]
  };

  // Breadcrumb schema for SEO
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.alphaapkstore.com/"
      }
    ]
  };

  // Article schema for latest blogs
  const articleSchema = latestBlogs.map((post, index) => ({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "url": `https://www.alphaapkstore.com/blog/${post.slug}`,
    "datePublished": post.date || "2026-06-01",
    "author": {
      "@type": "Organization",
      "name": "Alpha APK Store"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Alpha APK Store",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.alphaapkstore.com/logo.png"
      }
    }
  }));

  return (
    <div className="home-container">
      {/* SEO HELMET */}
      <Helmet>
        <html lang="en" />
        <title>Alpha APK Store | Safe APK Downloads for Android Apps & Games</title>
        <meta
          name="description"
          content="Download safe Android APKs from Alpha APK Store. Trusted APK downloads for games, tools, VPN, music, social media, and utility apps. Updated daily with verified package info, screenshots, and install guides."
        />
        <link rel="canonical" href="https://www.alphaapkstore.com/" />

        <link rel="preconnect" href="https://alpha-apk-backend.onrender.com" />
        <link rel="dns-prefetch" href="https://alpha-apk-backend.onrender.com" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Alpha APK Store" />
        <meta property="og:locale" content="en_US" />
        <meta
          property="og:title"
          content="Alpha APK Store | Safe APK Downloads for Android"
        />
        <meta
          property="og:description"
          content="Download safe Android APKs. Games, tools, VPN, social apps and more."
        />
        <meta property="og:url" content="https://www.alphaapkstore.com/" />
        <meta
          property="og:image"
          content="https://www.alphaapkstore.com/logo.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@alphaapkstore" />
        <meta
          name="twitter:title"
          content="Alpha APK Store | Safe APK Downloads for Android"
        />
        <meta
          name="twitter:description"
          content="Download safe Android APKs. Games, tools, VPN, social apps and more."
        />
        <meta
          name="twitter:image"
          content="https://www.alphaapkstore.com/logo.png"
        />

        {/* Schema scripts */}
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {articleSchema.map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* SIDE MENU BUTTON */}
      <div className="menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
        ☰
      </div>

      {/* SIDE MENU */}
      {menuOpen && (
        <nav className="side-menu">
          <Link to="/" onClick={() => setMenuOpen(false)}>
            Home
          </Link>
          <a href="/" onClick={() => window.location.reload()}>
            Reload Site
          </a>
          <a href="#trending" onClick={() => setMenuOpen(false)}>
            Trending Apps
          </a>
          <a href="#latest-articles" onClick={() => setMenuOpen(false)}>
            Latest Articles
          </a>
          <Link to="/about" onClick={() => setMenuOpen(false)}>
            About
          </Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)}>
            Contact
          </Link>
          <Link to="/blog" onClick={() => setMenuOpen(false)}>
            Blog
          </Link>
        </nav>
      )}

      {/* MOBILE TOP RIGHT */}
      <div className="top-right-actions">
        <button
          className="mobile-search-btn"
          onClick={() => {
            document
              .querySelector(".search-box")
              ?.classList.toggle("show-mobile-search");
          }}
        >
          🔍
        </button>
        </div> 
     {/* HEADER */}
<nav className="main-navigation">
  <header className="top-header">
    <div>
      <h1 className="site-title">
        Alpha APK Store™ - Safe APK Downloads for Android
      </h1>
      <p className="site-subtitle">
        Safe APK downloads • Latest versions • Fast access
      </p>
    </div>
    <div className="header-actions">
      {/* your buttons / search / icons go here */}
    </div>   {/* ← MISSING THIS CLOSING TAG */}
  </header>
</nav>

      {/* CATEGORY BUTTONS */}
      <div className="category-row">
        {categories.map((cat) => {
          const slug = cat === "All" ? "/" : `/${cat.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <button
              key={cat}
              className={`category-btn ${
                selectedCategory === cat ? "active-category" : ""
              }`}
              onClick={() => {
                setSelectedCategory(cat);
                navigate(slug);
              }}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* SEARCH */}
      <div className="search-box">
        <input
          type="text"
          placeholder="Search APKs like Termux, Spotify, ZArchiver..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* SEO H1 SECTION */}
      <section className="home-seo-section hero-seo-section">
        <h1 className="seo-main-heading">
          Safe APK Downloads for Android Apps & Games
        </h1>
        <p className="seo-hero-text">
          Discover and download verified Android APK files from Alpha APK Store.
          We offer a curated collection of safe APK downloads including popular
          apps like{" "}
          <Link to="/apk/spotify-apk-download-latest-version-for-android-free-music-podcasts">
            Spotify
          </Link>
          ,{" "}
          <Link to="/apk/termux-apk-download-latest-version-for-android-free-linux-terminal-emulator">
            Termux
          </Link>
          ,{" "}
          <Link to="/apk/zarchiver-apk-download-latest-version-for-android-free-zip-rar-file-manager">
            ZArchiver
          </Link>
          , and{" "}
          <Link to="/apk/newpipe-apk-download-latest-version-for-android-free-lightweight-video-player">
            NewPipe
          </Link>
          . All apps are organized by category with detailed version information, compatibility notes, and installation guides.
        </p>
      </section>

      {/* TRENDING */}
      <div className="trending-box" id="trending">
        <h2>🔥 Trending Downloads</h2>
        <p>
          Termux • Spotify • ZArchiver • Telegram • NewPipe • Instagram
        </p>
      </div>

      {/* DESKTOP APK GRID */}
      <div className="apk-grid desktop-grid">
        {filteredApks.map((apk) => (
          <Link to={`/apk/${apk.slug}`} className="apk-card" key={apk.id}>
            <img
              src={apk.image_url}
              alt={`${apk.name} APK download - ${apk.category} app`}
              loading="lazy"
              decoding="async"
              width="300"
              height="180"
            />
            <div className="card-badges">
              <span>{apk.category}</span>
              <span>{apk.version || "Latest"}</span>
            </div>
            <h3>{apk.name}</h3>
            <p>{apk.category}</p>
            <div className="meta">
              <p>⭐ {apk.rating || "4.5"}</p>
              <p>📦 {apk.size || "N/A"}</p>
              <p>🔄 {apk.version || "Latest"}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* MOBILE HORIZONTAL UI */}
      <div className="mobile-horizontal-wrapper">
        {[
          { title: "Recent APKs", data: apks.slice(0, 8) },
          {
            title: "Popular APKs",
            data: apks.filter((apk) =>
              ["spotify", "termux", "facebook", "telegram"].some((name) =>
                apk.name?.toLowerCase().includes(name)
              )
            ),
          },
          {
            title: "Games",
            data: apks.filter((apk) =>
              apk.category?.toLowerCase().includes("game")
            ),
          },
          {
            title: "Music Apps",
            data: apks.filter((apk) =>
              apk.category?.toLowerCase().includes("music")
            ),
          },
          {
            title: "Tools Apps",
            data: apks.filter((apk) =>
              apk.category?.toLowerCase().includes("tool")
            ),
          },
          {
            title: "Education Apps",
            data: apks.filter((apk) =>
              apk.category?.toLowerCase().includes("education")
            ),
          },
          {
            title: "VPN Apps",
            data: apks.filter((apk) =>
              apk.category?.toLowerCase().includes("vpn")
            ),
          },
        ].map(
          (section, index) =>
            section.data.length > 0 && (
              <div className="mobile-section" key={index}>
                <div className="section-header">
                  <h2>{section.title}</h2>
                </div>
                <div className="horizontal-scroll">
                  {section.data.map((apk) => (
                    <Link
                      to={`/apk/${apk.slug}`}
                      className="horizontal-card"
                      key={apk.id}
                    >
                      <img
                        src={apk.image_url}
                        alt={`${apk.name} APK`}
                        loading="lazy"
                        decoding="async"
                        width="300"
                        height="180"
                      />
                      <div className="mobile-card-badge">
                        {apk.version || "Latest"}
                      </div>
                      <div className="horizontal-info">
                        <h3>{apk.name}</h3>
                        <p>{apk.category}</p>
                        <span>⭐ {apk.rating || "4.5"}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )
        )}
      </div>

      {/* LATEST ARTICLES */}
      <section className="home-seo-section" id="latest-articles">
        <h2>Latest Android Articles</h2>
        <p>
          Read helpful Android guides, APK tutorials, music app comparisons,
          gaming tips, and safe APK download advice from Alpha APK Store.
        </p>
        <div className="home-blog-links">
          {latestBlogs.map((post) => (
            <Link
              to={`/blog/${post.slug}`}
              className="home-blog-card"
              key={post.slug}
            >
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <span>{post.category}</span>
            </Link>
          ))}
        </div>
        <Link to="/blog" className="read-more-btn">
          View All Articles
        </Link>
      </section>

      {/* SEO CONTENT SECTION */}
      <section className="home-seo-section">
        <h2>Welcome to Alpha APK Store</h2>
        <p>
          Alpha APK Store is a trusted platform where Android users can
          discover and download popular APK files safely and quickly. Our
          website provides updated APK versions for apps like{" "}
          <Link to="/apk/spotify-apk-download-latest-version-for-android-free-music-podcasts">
            Spotify
          </Link>
          ,{" "}
          <Link to="/apk/termux-apk-download-latest-version-for-android-free-linux-terminal-emulator">
            Termux
          </Link>
          ,{" "}
          <Link to="/apk/zarchiver-apk-download-latest-version-for-android-free-zip-rar-file-manager">
            ZArchiver
          </Link>
          , and{" "}
          <Link to="/apk/newpipe-apk-download-latest-version-for-android-free-lightweight-video-player">
            NewPipe
          </Link>
          , and many more.
        </p>
        <p>
          We focus on providing a clean and smooth experience for Android
          users looking for useful apps, tools, games, and utilities. Users
          can explore app screenshots, latest versions, update details, app
          categories, and download information easily.
        </p>
        <p>
          Alpha APK Store is designed especially for users who want a
          lightweight and fast Android APK browsing experience on both desktop
          and mobile devices.
        </p>
      </section>

      {/* WHY CHOOSE US */}
      <section className="home-seo-section">
        <h2>Why Choose Alpha APK Store?</h2>
        <ul>
          <li>Fast and simple APK downloads</li>
          <li>Mobile-friendly clean interface</li>
          <li>Updated Android applications</li>
          <li>Easy app discovery and search</li>
          <li>Popular apps and utility tools</li>
          <li>Safe browsing experience</li>
          <li>Smooth website performance</li>
          <li>Trusted Android app information</li>
        </ul>
      </section>

      {/* APK INSTALL GUIDE */}
      <section className="home-seo-section">
        <h2>How to Install APK Files on Android</h2>
        <p>
          Installing APK files on Android devices is very simple. First
          download the APK file from Alpha APK Store. Then open your
          Android settings and allow installation from unknown sources or
          unknown apps depending on your device.
        </p>
        <p>
          After enabling permission, open the downloaded APK file and tap
          install. Within a few seconds the application will be installed
          successfully on your Android phone.
        </p>
        <p>
          Many Android users prefer APK files to access app updates,
          lightweight apps, open-source applications, and useful tools
          outside the Play Store ecosystem.
        </p>
        <Link to="/blog/how-to-install-apk-files" className="read-more-btn">
          Read Full APK Install Guide
        </Link>
      </section>

      {/* POPULAR CATEGORIES */}
      <section className="home-seo-section">
        <h2>Popular APK Categories</h2>
        <p>
          Explore Android applications across multiple categories including
          music streaming apps, Android tools, social media apps, VPN
          applications, education apps, and lightweight games.
        </p>
        <div className="seo-category-links">
          <Link to="/games" className="seo-category-card">
            🎮 Games Apps
          </Link>
          <Link to="/tools" className="seo-category-card">
            🛠 Tools Apps
          </Link>
          <Link to="/music" className="seo-category-card">
            🎵 Music Apps
          </Link>
          <Link to="/social" className="seo-category-card">
            💬 Social Apps
          </Link>
          <Link to="/education" className="seo-category-card">
            📚 Education Apps
          </Link>
          <Link to="/vpn" className="seo-category-card">
            🔒 VPN Apps
          </Link>
        </div>
      </section>

      {/* FEATURED APPS SECTION */}
      <section className="home-seo-section">
        <h2>Featured Android APKs</h2>
        <p>
          Browse our most popular verified APK downloads. Each app page includes 
          detailed information, version history, and safe download instructions.
        </p>
        <div className="featured-apps-grid">
          {apks.slice(0, 6).map((apk) => (
            <Link 
              to={`/apk/${apk.slug}`} 
              className="featured-app-link" 
              key={apk.id}
            >
              <span className="featured-app-name">{apk.name}</span>
              <span className="featured-app-cat">{apk.category}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST SECTION */}
      <section className="home-seo-section">
        <h2>About Alpha APK Store</h2>
        <p>
          Alpha APK Store is focused on creating a smooth Android app
          discovery experience for users looking for useful applications,
          tools, games, and productivity apps.
        </p>
        <p>
          Our goal is to make Android app browsing easier with detailed
          information, screenshots, categories, update details, and organized
          navigation.
        </p>
        <p>
          We continue improving the website experience regularly for
          Android users around the world.
        </p>
        <Link to="/about" className="read-more-btn">
          Learn More About Us
        </Link>
      </section>

      {/* FOOTER */}
      <footer className="pro-footer">
        <div className="footer-box">
          <div className="footer-left">
            <img
              src="/logo.png"
              alt="Alpha APK Store Logo"
              className="footer-logo"
              loading="lazy"
              width="120"
              height="40"
            />
            <div className="footer-brand">
              <h2>ALPHA APK STORE</h2>
              <p>Safe APK downloads • Trusted updates • Fast access</p>
              <p>© 2026 Alpha APK Store. All Rights Reserved.</p>
              <p>Founded by KARANDEEP SINGH SANDHU</p>
              <p>Contact: alphaapkstore@gmail.com</p>
              <p>
                DMCA: We respect copyright owners and respond to valid
                removal requests.
              </p>
              <p>
                Disclaimer: Alpha APK Store does not claim ownership of
                third-party apps, logos, or trademarks.
              </p>
            </div>
          </div>
          <div className="footer-links">
  <Link to="/about">About</Link>
  <Link to="/contact">Contact</Link>
  <Link to="/privacy-policy">Privacy Policy</Link>
  <Link to="/terms">Terms</Link>
  <Link to="/blog">Blog</Link>
  <Link to="/faq">FAQ</Link>
  <Link to="/security-trust">Security & Trust</Link>
  <Link to="/dmca">DMCA</Link>
  <a
    href="https://www.youtube.com/@alphaapkstore"
    target="_blank"
    rel="noopener noreferrer"
  >
    YouTube
  </a>
  <a
    href="https://pin.it/4iXPuvWFH"
    target="_blank"
    rel="noopener noreferrer"
  >
    Pinterest
  </a>
  {/* ADD THIS */}
  <a
    href="https://www.instagram.com/alphaapkstore/"
    target="_blank"
    rel="noopener noreferrer"
  >
    Instagram
  </a>
  {/* END ADD */}
  <a href="mailto:alphaapkstore@gmail.com">Email</a>
</div>
        </div>
      </footer>

      {/* COOKIE POPUP */}
      {showCookie && (
        <div className="cookie-box">
          <p>
            We use cookies to improve your browsing experience, analyze
            traffic, and support website features.
          </p>
          <div className="cookie-actions">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <button onClick={acceptCookies}>Accept</button>
          </div>
        </div>
      )}
    </div>
  );
}